/**
 * How one extracted card field should read on screen (#309).
 *
 * The pane used to render every unfound field as the literal word "unreadable"
 * with a Needs-review chip. Most cards do not print a date of birth at all, so
 * that chip fired on the majority of reads for a non-problem — and a chip that
 * is usually wrong is worse than no chip, because the one that matters arrives
 * in the same yellow as the noise. The chip's job is to be believed.
 *
 * Pure, so the three states are pinned by tests rather than by reading JSX.
 */

export type CardFieldRead =
  /** We read it off the card. */
  | { kind: 'read'; value: string }
  /** The card does not print this field. Nothing is wrong; nothing to review. */
  | { kind: 'not_on_card' }
  /** Printed but illegible — blurred, glare, cropped. A real extraction
   *  failure, and the only state that earns the review chip. */
  | { kind: 'unreadable' }
  /** We did not get it and cannot say which kind of missing this was: a read
   *  stored before the distinction existed, or a field we already hold from the
   *  appointment record so the card never had to answer for it. Reads
   *  honestly ("not read") and never raises a flag we cannot justify. */
  | { kind: 'unknown' }

export interface CardFieldInput {
  /** The extracted value, if any. */
  value: string | null
  /** The read's own `found` flag for this field. */
  found: boolean | undefined
  /** The read's `absent` verdict: true = not printed, false = illegible,
   *  undefined = no verdict (pre-#309 read). */
  absent: boolean | undefined
  /**
   * We already hold this value from the appointment record (the DOB sitting in
   * the case header, say). Then the card not carrying it is not a question, and
   * flagging it sends staff to look at a non-problem — the exact complaint in
   * the ticket, where the DOB was two inches above the chip.
   */
  heldOnRecord?: boolean
}

export function cardFieldRead(input: CardFieldInput): CardFieldRead {
  const value = (input.value ?? '').trim()
  if (input.found && value) return { kind: 'read', value }
  if (input.absent === true) return { kind: 'not_on_card' }
  // Held on record outranks "illegible": we are not missing anything, so there
  // is nothing to send anyone to review.
  if (input.heldOnRecord) return { kind: 'unknown' }
  if (input.absent === false) return { kind: 'unreadable' }
  return { kind: 'unknown' }
}

/** Only a genuine extraction failure earns the Needs-review chip. */
export function needsReview(state: CardFieldRead): boolean {
  return state.kind === 'unreadable'
}

/** What the value column shows. */
export function cardFieldLabel(state: CardFieldRead): string {
  switch (state.kind) {
    case 'read': return state.value
    case 'not_on_card': return 'Not printed on this card'
    case 'unreadable': return "Couldn't read it"
    case 'unknown': return 'Not read'
  }
}
